import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CardMedia, Grid, MenuItem, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useParams } from "react-router-dom";

import {
  addNetworkPortalMessage,
  acceptNetworkPortalAssignment,
  createNetworkPortalAssignmentSubmission,
  declineNetworkPortalAssignment,
  getNetworkPortalMessageAttachment,
  getNetworkPortalAssignment,
  markNetworkPortalMessagesRead,
  startNetworkPortalAssignment,
  uploadNetworkPortalMessageAttachments,
  uploadNetworkPortalAssignmentFiles,
  updateNetworkPortalAssignmentStatus,
} from "../../services/coreNetworkPortal";
import {
  NetworkPortalLayout,
  PortalAuthState,
  PortalLoading,
  StatusChip,
  formatDateTime,
  safeArray,
  useNetworkPortalSession,
} from "./networkPortalShared";
import {
  AssignmentActionsCard,
  AssignmentBreadcrumb,
  AssignmentHero,
  AssignmentPanel,
  AssignmentSummary,
} from "./components/TalentAssignmentDetailUi";
import "./TalentAssignmentDetail.css";
import { getCoreNetworkMessageAttachment } from "../../services/coreNetwork";

function getSubmissionWorkflowState(submission) {
  const status = String(submission?.status || "").trim().toLowerCase();
  return {
    status,
    approved: ["approved", "completed"].includes(status),
    revisionRequested: ["revision_requested", "rejected", "cancelled"].includes(status),
    awaitingReview: ["submitted", "pending", "under_review", "in_review"].includes(status),
  };
}

export default function NetworkPortalAssignmentDetail({ previewData = null, previewInstanceUniqueId = null }) {
  const previewMode = Boolean(previewData);
  const { id } = useParams();
  // Portal authentication is never replaced by an administrator's credentials.
  // Preview data comes only from the separate admin-authorized read endpoint.
  const { member: talentMember, loading, error } = useNetworkPortalSession({ disabled: previewMode });
  const member = previewMode ? previewData?.member : talentMember;
  const [loadedDetail, setDetail] = useState(null);
  const detail = previewMode ? previewData : loadedDetail;
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [busy, setBusy] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [submissionNotes, setSubmissionNotes] = useState("");
  const [providerStatus, setProviderStatus] = useState("in_production");
  const [providerNote, setProviderNote] = useState("");
  const [providerAttachments, setProviderAttachments] = useState("");
  const [submissionSelectedFiles, setSubmissionSelectedFiles] = useState([]);
  const [uploadedSubmissionFiles, setUploadedSubmissionFiles] = useState([]);
  const [messageSelectedFiles, setMessageSelectedFiles] = useState([]);
  const [uploadedMessageFiles, setUploadedMessageFiles] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (previewMode || !member) return;
    let active = true;

    async function loadDetail() {
      try {
        setPageLoading(true);
        setPageError("");
        const response = await getNetworkPortalAssignment(id);
        await markNetworkPortalMessagesRead(response?.workRequest?.id, { assignmentId: response?.assignment?.id });
        if (active) {
          setDetail(response);
          setUploadedSubmissionFiles([]);
          setUploadedMessageFiles([]);
        }
      } catch (loadError) {
        if (active) {
          setPageError(loadError?.response?.data?.message || loadError.message || "Unable to load assignment.");
        }
      } finally {
        if (active) {
          setPageLoading(false);
        }
      }
    }

    loadDetail();
    return () => {
      active = false;
    };
  }, [id, member, previewMode]);

  if (!previewMode && (loading || pageLoading)) return <PortalLoading />;
  if (!member) return <PortalAuthState error={error} />;
  if (!detail) {
    return <NetworkPortalLayout title="Assignment" member={member}><Alert severity="error">{pageError || "Assignment not found."}</Alert></NetworkPortalLayout>;
  }

  const { assignment, workRequest, messages = [], submissions = [], providerStatusHistory = [] } = detail;
  const assignmentDetail = detail?.detail || {};
  const requestDescription = assignmentDetail.description || workRequest?.description || null;
  const requestInstructions = assignmentDetail.instructions || workRequest?.instructions || null;
  const shouldShowInstructions = requestInstructions && String(requestInstructions).trim() !== String(requestDescription || "").trim();
  const salesOrderLink = getSalesOrderLink(assignmentDetail.order);
  const latestSubmission = submissions[0] || null;
  const unreadMessageCount = Number(detail?.unreadMessageCount || 0);
  const submissionState = getSubmissionWorkflowState(latestSubmission);

  async function refresh() {
    const response = await getNetworkPortalAssignment(id);
    setDetail(response);
    setUploadedSubmissionFiles([]);
    setSubmissionSelectedFiles([]);
    setUploadedMessageFiles([]);
    setMessageSelectedFiles([]);
  }

  async function runAction(actionKey, callback, { refreshAfter = true } = {}) {
    if (previewMode) return;
    try {
      setBusy(actionKey);
      setPageError("");
      setSuccessMessage("");
      await callback();
      if (refreshAfter) await refresh();
    } catch (actionError) {
      setPageError(actionError?.response?.data?.message || actionError.message || "Action failed.");
    } finally {
      setBusy("");
    }
  }

  function formatFileSize(value) {
    const size = Number(value);
    if (!Number.isFinite(size) || size <= 0) return "-";
    if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    if (size >= 1024) return `${Math.round(size / 1024)} KB`;
    return `${size} B`;
  }

  function displayFileName(file) {
    return file?.fileName || file?.name || file?.url || file?.downloadUrl || "Attachment";
  }

  function fileLink(file) {
    return file?.downloadUrl || file?.previewUrl || file?.url || file?.path || file?.internalFilePath || "";
  }

  function isImageFile(file) {
    const href = fileLink(file).toLowerCase();
    return /\.(png|jpe?g|gif|webp|svg)(\?|#|$)/.test(href);
  }

  function displayValue(value) {
    if (value === true) return "Yes";
    if (value === false) return "No";
    if (value === null || value === undefined || value === "") return "Not provided";
    return value;
  }

  function getSalesOrderLink(order = {}) {
    const documentType = String(order?.documentType || "").trim().toLowerCase();
    const salesOrderId = String(order?.documentId || order?.salesOrderId || "").trim();
    if (documentType !== "sales-order" || !salesOrderId) return "";
    return `/generated-sales-order/${salesOrderId}`;
  }

  function renderDetailRows(rows) {
    return (
      <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
        {rows.filter((row) => row.value !== null && row.value !== undefined && row.value !== "").map((row) => (
          <Box key={row.label}>
            <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{row.label}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, wordBreak: "break-word" }}>{displayValue(row.value)}</Typography>
          </Box>
        ))}
      </Box>
    );
  }

  function renderArtworkFiles(files, emptyLabel) {
    const items = safeArray(files);
    if (!items.length) {
      return <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>;
    }
    return (
      <Grid container spacing={2}>
        {items.map((file, index) => {
          const href = fileLink(file);
          const label = displayFileName(file);
          return (
            <Grid item xs={12} sm={6} md={4} key={`${label}-${href || index}`}>
              <Card elevation={0} sx={{ height: "100%", border: "1px solid #edf2f7", borderRadius: 2, overflow: "hidden" }}>
                {href && isImageFile(file) ? (
                  <CardMedia component="img" image={href} alt={label} sx={{ height: 148, objectFit: "contain", backgroundColor: "#f8fafc" }} />
                ) : null}
                <CardContent sx={{ p: 1.5 }}>
                  <Stack spacing={0.75}>
                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>{label}</Typography>
                    {file?.placementName || file?.placementCode ? (
                      <Typography variant="caption" color="text.secondary">{[file.placementName, file.placementCode].filter(Boolean).join(" · ")}</Typography>
                    ) : null}
                    {file?.description ? <Typography variant="caption" color="text.secondary">{file.description}</Typography> : null}
                    {href ? <Button size="small" variant="outlined" href={href} target="_blank" rel="noopener noreferrer">Open file</Button> : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  async function openMessageAttachment(message, attachment) {
    const response = previewMode
      ? await getCoreNetworkMessageAttachment(message.id, attachment.fileId, { instanceUniqueId: previewInstanceUniqueId })
      : await getNetworkPortalMessageAttachment(message.id, attachment.fileId);
    const url = response?.attachment?.downloadUrl || response?.attachment?.previewUrl;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

  function renderFiles(files, emptyLabel) {
    const items = safeArray(files);
    if (!items.length) {
      return <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>;
    }

    return items.map((file, index) => {
      const href = fileLink(file);
      const label = displayFileName(file);
      return (
        <Stack
          key={`${label}-${file?.fileId || href || index}`}
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{ py: 0.5 }}
        >
          {typeof file?.onOpen === "function" ? (
            <Button variant="text" size="small" onClick={file.onOpen} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
              {label}
            </Button>
          ) : href ? (
            <Typography
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ color: "primary.main", textDecoration: "none", wordBreak: "break-word" }}
            >
              {label}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{label}</Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(file?.fileSize)}
          </Typography>
        </Stack>
      );
    });
  }

  const assignmentStatus = assignmentDetail.status || assignment.status;
  const assignmentTitle = workRequest?.title || `Assignment #${assignment.id}`;
  const recentProviderHistory = safeArray(providerStatusHistory).slice(0, 3);

  const handleDecline = () => {
    if (previewMode) return;
    const reason = window.prompt("Reason for declining this assignment?", "");
    if (reason === null) return;
    runAction("decline", () => declineNetworkPortalAssignment(assignment.id, reason));
  };

  const providerStatusForm = (
    <Stack spacing={1.5}>
      <TextField
        select
        fullWidth
        size="small"
        label="Provider status"
        value={providerStatus}
        onChange={(event) => setProviderStatus(event.target.value)}
      >
        {["art_review", "proof_required", "proof_sent", "proof_approved", "queued", "in_production", "quality_check", "packed", "shipped", "delivered", "revision_requested", "cancelled"].map((option) => (
          <MenuItem key={option} value={option}>{option.replace(/[_-]+/g, " ")}</MenuItem>
        ))}
      </TextField>
      <TextField fullWidth size="small" label="Status note" value={providerNote} onChange={(event) => setProviderNote(event.target.value)} multiline minRows={2} />
      <TextField
        fullWidth
        size="small"
        label="Attachment URLs"
        helperText="Comma-separated file URLs or references"
        value={providerAttachments}
        onChange={(event) => setProviderAttachments(event.target.value)}
      />
      <Button
        variant="outlined"
        disabled={busy === "status"}
        onClick={() => runAction("status", async () => {
          const attachmentsJson = providerAttachments.split(",").map((item) => item.trim()).filter(Boolean).map((url) => ({ url }));
          await updateNetworkPortalAssignmentStatus(assignment.id, { providerStatus, note: providerNote || null, attachmentsJson });
          setProviderNote("");
          setProviderAttachments("");
          setSuccessMessage("Provider status updated.");
        })}
      >
        {busy === "status" ? "Updating..." : "Update status"}
      </Button>
    </Stack>
  );

  const submissionStatusAlert = latestSubmission ? (
    <Alert severity={submissionState.approved ? "success" : submissionState.revisionRequested ? "warning" : submissionState.awaitingReview ? "info" : "info"}>
      {submissionState.approved
        ? "The latest submission has already been approved."
        : submissionState.revisionRequested
          ? `Changes were requested for the latest submission.${latestSubmission.reviewNotes ? ` ${latestSubmission.reviewNotes}` : ""}`
          : `Latest submission status: ${latestSubmission.status.replace(/[_-]+/g, " ")}.`}
    </Alert>
  ) : null;

  const canUploadSubmission = !submissionState.approved;
  const submissionPrimaryLabel = submissionState.revisionRequested ? "Submit revised work" : "Submit work";

  const submissionForm = (
    <Stack spacing={1.5}>
      {submissionStatusAlert}
      <Typography variant="caption" color="text.secondary">Accepted: PDF, AI, EPS, PS, CDR, ZIP, SVG, PNG, JPG, WEBP and GIF. Maximum 10 files, 25 MB per file. Upload files, then click Submit Work to send them for review.</Typography>
      {canUploadSubmission ? (
        <>
          <Button variant="outlined" component="label" disabled={busy === "upload" || busy === "submission"}>
            Select files
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.ai,.eps,.ps,.cdr,.zip,.svg,.png,.jpg,.jpeg,.webp,.gif"
              onChange={(event) => setSubmissionSelectedFiles(Array.from(event.target.files || []))}
            />
          </Button>
          {submissionSelectedFiles.length ? (
            <Box sx={{ p: 1.5, borderRadius: 2, border: "1px dashed #cbd5e0", backgroundColor: "#f8fafc" }}>
              <Stack spacing={0.75}>
                {submissionSelectedFiles.map((file) => (
                  <Stack key={`${file.name}-${file.size}`} direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{file.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: "0 0 auto" }}>{formatFileSize(file.size)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ) : null}
          {uploadedSubmissionFiles.length ? renderFiles(uploadedSubmissionFiles, "No uploaded files.") : null}
          <Stack direction={{ xs: "column", sm: "row", lg: "column" }} spacing={1}>
            <Button
              variant="outlined"
              disabled={busy === "upload" || busy === "submission" || !submissionSelectedFiles.length}
              onClick={() => runAction("upload", async () => {
                const response = await uploadNetworkPortalAssignmentFiles(assignment.id, submissionSelectedFiles);
                setUploadedSubmissionFiles(safeArray(response.files));
                setSubmissionSelectedFiles([]);
                setSuccessMessage(`Uploaded ${safeArray(response.files).length} file${safeArray(response.files).length === 1 ? "" : "s"} for this assignment.`);
              }, { refreshAfter: false })}
            >
              {busy === "upload" ? "Uploading..." : "Upload files"}
            </Button>
            <Button
              variant="text"
              disabled={busy === "upload" || busy === "submission" || (!submissionSelectedFiles.length && !uploadedSubmissionFiles.length)}
              onClick={() => {
                setSubmissionSelectedFiles([]);
                setUploadedSubmissionFiles([]);
                setSuccessMessage("");
              }}
            >
              Clear pending files
            </Button>
          </Stack>
          <TextField fullWidth size="small" label="Submission notes" value={submissionNotes} onChange={(event) => setSubmissionNotes(event.target.value)} multiline minRows={3} />
          <Button
            variant="contained"
            disabled={busy === "submission" || (!uploadedSubmissionFiles.length && !submissionSelectedFiles.length)}
            onClick={() => runAction("submission", async () => {
              await createNetworkPortalAssignmentSubmission(assignment.id, {
                files: submissionSelectedFiles,
                filesJson: uploadedSubmissionFiles,
                notes: submissionNotes || null,
              });
              setSubmissionSelectedFiles([]);
              setUploadedSubmissionFiles([]);
              setSubmissionNotes("");
              setSuccessMessage("Submission uploaded successfully.");
            })}
          >
            {busy === "submission" ? "Submitting..." : submissionPrimaryLabel}
          </Button>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No new upload is required right now because the latest submission is already approved.
        </Typography>
      )}
    </Stack>
  );

  const providerHistory = (items, compact = false) => safeArray(items).length ? (
    <div className={compact ? "talent-assignment-detail__compact-history" : ""}>
      {safeArray(items).map((entry) => (
        <div className="talent-assignment-detail__history-item" key={entry.id}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <StatusChip value={entry.providerStatus} />
              <Typography variant="caption" color="text.secondary">{formatDateTime(entry.createdAt)}</Typography>
            </Stack>
            {entry.actorDisplayName || entry.actorName ? <Typography variant="caption" sx={{ fontWeight: 700 }}>{entry.actorDisplayName || entry.actorName}</Typography> : null}
            <Typography variant="body2">{entry.note || "Status updated."}</Typography>
            {safeArray(entry.attachmentsJson).length ? renderFiles(entry.attachmentsJson, "No attachments.") : null}
          </Stack>
        </div>
      ))}
    </div>
  ) : <Typography variant="body2" color="text.secondary">No provider production updates have been posted yet.</Typography>;

  const messagesPanel = (
    <Stack spacing={2}>
      {unreadMessageCount ? <Alert severity="info">{unreadMessageCount} unread message{unreadMessageCount === 1 ? "" : "s"}.</Alert> : null}
      {messages.length ? messages.map((message) => (
        <Box key={message.id} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf2f7", backgroundColor: "#fbfcfd" }}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <StatusChip value={message.senderType} />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{message.senderDisplayName || "Sender"}</Typography>
              {!message.isRead ? <StatusChip value="unread" /> : null}
              <Typography variant="caption" color="text.secondary">{formatDateTime(message.createdAt)}</Typography>
            </Stack>
            {message.messageBody ? <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{message.messageBody}</Typography> : null}
            {safeArray(message.attachmentsJson).length ? renderFiles(safeArray(message.attachmentsJson).map((attachment) => ({
              ...attachment,
              onOpen: () => openMessageAttachment(message, attachment),
            })), "No attachments.") : null}
          </Stack>
        </Box>
      )) : <Typography variant="body2" color="text.secondary">No messages have been posted yet.</Typography>}
      <Button variant="outlined" component="label" disabled={busy === "message-upload" || busy === "message"} sx={{ alignSelf: "flex-start" }}>
        Select attachments
        <input type="file" hidden multiple accept=".pdf,.ai,.eps,.ps,.svg,.png,.jpg,.jpeg,.webp,.gif,.zip" onChange={(event) => setMessageSelectedFiles(Array.from(event.target.files || []))} />
      </Button>
      {messageSelectedFiles.length ? (
        <Stack spacing={0.75}>
          {messageSelectedFiles.map((file) => <Typography key={`${file.name}-${file.size}`} variant="caption" color="text.secondary">{file.name}</Typography>)}
          <Button
            variant="text"
            sx={{ alignSelf: "flex-start" }}
            disabled={busy === "message-upload"}
            onClick={() => runAction("message-upload", async () => {
              const response = await uploadNetworkPortalMessageAttachments(workRequest.id, messageSelectedFiles, { assignmentId: assignment.id });
              setUploadedMessageFiles(safeArray(response.files));
              setMessageSelectedFiles([]);
            }, { refreshAfter: false })}
          >
            {busy === "message-upload" ? "Uploading..." : "Upload attachments"}
          </Button>
        </Stack>
      ) : null}
      {uploadedMessageFiles.length ? renderFiles(uploadedMessageFiles, "No uploaded message files.") : null}
      <TextField fullWidth label="Message" multiline minRows={3} value={messageBody} onChange={(event) => setMessageBody(event.target.value)} />
      <Button
        variant="contained"
        sx={{ alignSelf: { xs: "stretch", sm: "flex-end" } }}
        disabled={busy === "message" || (!messageBody.trim() && !uploadedMessageFiles.length)}
        onClick={() => runAction("message", async () => {
          await addNetworkPortalMessage(workRequest.id, { assignmentId: assignment.id, messageBody, attachmentsJson: uploadedMessageFiles });
          setMessageBody("");
          setUploadedMessageFiles([]);
          setSuccessMessage("Message sent.");
        })}
      >
        {busy === "message" ? "Sending..." : "Send message"}
      </Button>
    </Stack>
  );

  const submissionsPanel = (
    <Stack spacing={1.5}>
      {submissions.length ? submissions.map((submission) => (
        <Box key={submission.id} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf2f7", backgroundColor: "#fbfcfd" }}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <StatusChip value={submission.status} />
              <Typography variant="caption" color="text.secondary">v{Number(submission.version || 1)}</Typography>
              <Typography variant="caption" color="text.secondary">{formatDateTime(submission.submittedAt)}</Typography>
            </Stack>
            <Typography variant="body2">{submission.notes || "No submission notes."}</Typography>
            {submission.reviewNotes ? <Typography variant="caption" color="text.secondary">Review: {submission.reviewNotes}</Typography> : null}
            {renderFiles(submission.filesJson, "No files attached.")}
          </Stack>
        </Box>
      )) : <Typography variant="body2" color="text.secondary">No submissions have been uploaded yet.</Typography>}
      {submissionStatusAlert}
    </Stack>
  );

  const content = (
      <div className="talent-assignment-detail">
        <AssignmentBreadcrumb title={assignmentTitle} to={previewMode ? `/instance/${previewInstanceUniqueId}/core-network/work-requests/${workRequest.id}?tab=assignments` : "/talent/assignments"} />
        {pageError ? <Alert severity="error">{pageError}</Alert> : null}
        {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

        <div className="talent-assignment-detail__workspace">
          <div className="talent-assignment-detail__main">
            <AssignmentHero assignment={assignment} detail={assignmentDetail} workRequest={workRequest} member={member} previewMode={previewMode} />
            <AssignmentSummary items={[
              { icon: "due", label: "Due date", value: formatDateTime(assignmentDetail.deadlineAt || workRequest?.deadlineAt, "Not provided") },
              { icon: "requester", label: "Requester / Broker", value: assignmentDetail.requester?.name || "Not provided" },
              { icon: "activity", label: "Last activity", value: formatDateTime(assignmentDetail.lastActivityAt || workRequest?.lastActivityAt, "Not provided") },
            ]} />

            <AssignmentPanel title="Request / Brief">
              <Typography variant="body2" className="talent-assignment-detail__brief">
                {requestDescription || requestInstructions || "No request brief was supplied."}
              </Typography>
              {shouldShowInstructions ? (
                <Box sx={{ mt: 2, p: 2, borderRadius: 2, backgroundColor: "#f8fafc", border: "1px solid #edf2f7" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Instructions</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>{requestInstructions}</Typography>
                </Box>
              ) : null}
            </AssignmentPanel>

            <div className="talent-assignment-detail__two-up">
              <AssignmentPanel title="Products">
                <Stack spacing={1.5}>
                  {safeArray(assignmentDetail.products).length ? safeArray(assignmentDetail.products).map((product, index) => (
                    <Box key={`${product.sku || product.name || index}`} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #edf2f7" }}>
                      <Stack spacing={1}>
                        {product.productImageUrl ? <CardMedia component="img" image={product.productImageUrl} alt={product.name || "Product"} sx={{ maxHeight: 130, objectFit: "contain", backgroundColor: "#f8fafc", borderRadius: 1 }} /> : null}
                        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{product.name || "Product"}</Typography>
                        {renderDetailRows([
                          { label: "SKU", value: product.sku },
                          { label: "Variant / Garment", value: product.garment },
                          { label: "Category", value: product.category },
                          { label: "Quantity", value: product.quantity },
                          { label: "Priority", value: product.priority || assignmentDetail.priority || workRequest?.priority },
                        ])}
                      </Stack>
                    </Box>
                  )) : <Typography variant="body2" color="text.secondary">No product information was included.</Typography>}
                </Stack>
              </AssignmentPanel>

              <AssignmentPanel title="Artwork & Decoration">
                <Stack spacing={1.5}>
                  {safeArray(assignmentDetail.artwork?.placements).length ? safeArray(assignmentDetail.artwork.placements).map((artwork, index) => (
                    <Box key={`${artwork.placementCode || artwork.placementName || index}`} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #edf2f7" }}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {artwork.decorationType ? <StatusChip value={artwork.decorationType} /> : null}
                          {artwork.processType ? <StatusChip value={artwork.processType} /> : null}
                          {artwork.outputFormat ? <StatusChip value={artwork.outputFormat} /> : null}
                        </Stack>
                        {renderDetailRows([
                          { label: "Artwork", value: artwork.description },
                          { label: "Service / Decoration", value: artwork.decorationType || artwork.processType },
                          { label: "Placement", value: artwork.placementName },
                          { label: "Placement code", value: artwork.placementCode },
                          { label: "Dimensions", value: artwork.width && artwork.height ? `${artwork.width} × ${artwork.height} ${artwork.measurementUnit || ""}`.trim() : null },
                          { label: "Colours", value: safeArray(artwork.colors).join(", ") },
                          { label: "Change request", value: artwork.changeRequest },
                          { label: "Placement instructions", value: artwork.instructions },
                        ])}
                      </Stack>
                    </Box>
                  )) : <Typography variant="body2" color="text.secondary">No artwork or decoration information was included.</Typography>}
                </Stack>
              </AssignmentPanel>
            </div>

            <AssignmentPanel title="Source artwork files" className="talent-assignment-detail__file-grid">
              {renderArtworkFiles(assignmentDetail.artwork?.files || workRequest?.filesJson, "No source artwork was included with this request.")}
            </AssignmentPanel>

            <AssignmentPanel className="talent-assignment-detail__tabs">
              <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Assignment collaboration sections">
                <Tab label={`Messages${messages.length ? ` (${messages.length})` : ""}`} id="assignment-tab-0" aria-controls="assignment-tabpanel-0" />
                <Tab label={`Submissions${submissions.length ? ` (${submissions.length})` : ""}`} id="assignment-tab-1" aria-controls="assignment-tabpanel-1" />
                <Tab label={`Activity${providerStatusHistory.length ? ` (${providerStatusHistory.length})` : ""}`} id="assignment-tab-2" aria-controls="assignment-tabpanel-2" />
              </Tabs>
              <div role="tabpanel" id={`assignment-tabpanel-${activeTab}`} aria-labelledby={`assignment-tab-${activeTab}`}>
                {activeTab === 0 ? (
                  <fieldset disabled={previewMode} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                    {messagesPanel}
                  </fieldset>
                ) : null}
                {activeTab === 1 ? submissionsPanel : null}
                {activeTab === 2 ? (
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 900 }}>Provider status history</Typography>
                      {providerHistory(providerStatusHistory)}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 900 }}>Assignment details</Typography>
                      {renderDetailRows([
                        { label: "Fulfillment provider", value: assignmentDetail.fulfillment?.provider },
                        { label: "Fulfillment mode", value: assignmentDetail.fulfillment?.mode },
                        { label: "Source module", value: assignmentDetail.fulfillment?.sourceModule },
                        { label: "Document type", value: assignmentDetail.order?.documentType },
                        { label: "Document ID", value: assignmentDetail.order?.documentId },
                        { label: "Project", value: assignmentDetail.order?.orderId },
                        { label: "Sales order", value: assignmentDetail.order?.salesOrderId },
                        {
                          label: "Sales order link",
                          value: salesOrderLink ? (
                            <Button
                              size="small"
                              variant="text"
                              href={salesOrderLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ px: 0, minWidth: 0, fontWeight: 700, textTransform: "none" }}
                            >
                              Open sales order
                            </Button>
                          ) : null,
                        },
                        { label: "Purchase order", value: assignmentDetail.order?.purchaseOrderNumber || assignmentDetail.order?.purchaseOrderId },
                      ])}
                    </Box>
                  </Stack>
                ) : null}
              </div>
            </AssignmentPanel>
          </div>

          <aside className="talent-assignment-detail__rail" aria-label="Assignment actions and status">
            <AssignmentActionsCard
              status={assignmentStatus}
              busy={previewMode ? "preview" : busy}
              onAccept={() => runAction("accept", () => acceptNetworkPortalAssignment(assignment.id))}
              onStart={() => runAction("start", () => startNetworkPortalAssignment(assignment.id))}
              onDecline={handleDecline}
            />
            <AssignmentPanel
              title={submissionState.revisionRequested ? "Revise submission" : "Submissions"}
              subtitle={submissionState.revisionRequested ? "Update the work based on the latest review feedback." : "Upload deliverables, then submit them for review."}
            >
              <fieldset disabled={previewMode} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                {submissionForm}
              </fieldset>
            </AssignmentPanel>
            <AssignmentPanel title="Update provider status">
              <fieldset disabled={previewMode} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                {providerStatusForm}
              </fieldset>
            </AssignmentPanel>
            <AssignmentPanel
              title="Provider Status History"
              actions={providerStatusHistory.length > 3 ? <Button size="small" onClick={() => setActiveTab(2)}>View all</Button> : null}
            >
              {providerHistory(recentProviderHistory, true)}
            </AssignmentPanel>
          </aside>
        </div>
      </div>
  );
  // The real Talent shell supplies the .talent-portal style scope. In a
  // read-only admin modal we keep that scope (and the exact assignment UI)
  // without mounting its sidebar, authentication or navigation shell.
  if (previewMode) {
    return (
      <section className="talent-portal talent-portal--assignment-preview" aria-label="Read-only Talent assignment preview">
        {content}
      </section>
    );
  }
  return (
    <NetworkPortalLayout title="Assignment" subtitle="Review your assigned work and collaborate with the requester." member={member}>
      {content}
    </NetworkPortalLayout>
  );
}
